<div class="content-group-container">
  <!-- Anchor elements for secondary issue IDs so scrollToIssue() can find any issue in this group -->
  @for (anchorIssue of secondaryIssues; track anchorIssue) {
    <div [id]="'issue-' + anchorIssue.issueId" class="issue-anchor"></div>
  }

  <!-- Flat mode: no nested accordion, content rendered directly -->
  @if (flat) {
    <div class="flat-panel">
      <ng-container *ngTemplateOutlet="panelContent"></ng-container>
    </div>
  } @else {
    <button class="accordion" (click)="togglePanel()" [ngClass]="{ expanded: panelDisplayed }">
      <div class="accordion-title">
        <div class="issue-header-row">
          <div class="issue-type">{{ accordionLabel }}</div>
          @if (subGroup.issues.length > 1) {
            <span class="affected-count">{{ subGroup.issues.length }}</span>
          }
        </div>
      </div>
      <div class="header-end">
        @if (resourceTypeLabel) {
          <span class="header-resource-types">{{ resourceTypeLabel }}</span>
        }
        @if (subGroup.issues.length === 1) {
          <div class="header-resource">
            @if (resourceNameString(subGroup.issues[0])) {
              <span class="inline-copy-block" (click)="$event.stopPropagation()">
                <span
                  class="inline-copy-text"
                  [class.cell-expanded]="headerChipExpanded"
                  (click)="toggleHeaderChipExpand($event)"
                  >{{ resourceNameString(subGroup.issues[0]) }}</span
                >
              </span>
              <!-- Locate in resources table -->
              <button
                class="locate-btn"
                (click)="$event.stopPropagation(); resourceClicked.emit(resourceNameString(subGroup.issues[0]))"
                pTooltip="Find in resources table"
                tooltipPosition="top"
              >
                <svg lucideSearch></svg>
              </button>
              @if (getConsoleUrl(subGroup.issues[0])) {
                <a
                  class="console-link-btn"
                  href="{{ getConsoleUrl(subGroup.issues[0]) }}"
                  target="_blank"
                  rel="noopener"
                  (click)="$event.stopPropagation()"
                  [pTooltip]="'Open in ' + (application?.type === 'azure' ? 'Azure Portal' : 'AWS Console')"
                  tooltipPosition="top"
                >
                  <svg lucideExternalLink></svg>
                </a>
              }
              <!-- Issue ID copy lives in the expanded panel's timestamp·ID chip, not here:
                   a bare copy button beside the ARN's copy button read as duplicate icons.
                   ARN gets the only header copy button, matching the per-row pattern. -->
              @if (subGroup.issues[0].arn) {
                <button
                  type="button"
                  class="inline-copy-btn"
                  [copy-clipboard]="subGroup.issues[0].arn"
                  (copied)="onResourceArnCopied(0)"
                  (click)="$event.stopPropagation()"
                  [pTooltip]="copyFeedback.copiedStates['arn-0'] ? 'Copied' : subGroup.issues[0].arn"
                  tooltipPosition="top"
                >
                  @if (copyFeedback.copiedStates['arn-0']) {
                    <svg lucideCheck class="copy-check"></svg>
                  } @else {
                    <svg lucideCopy></svg>
                  }
                </button>
              }
            } @else {
              <span class="no-resource-text">No associated resource</span>
            }
          </div>
        }
        <div class="expand-glyph">
          @if (panelDisplayed) {
            <svg lucideChevronUp></svg>
          } @else {
            <svg lucideChevronDown></svg>
          }
        </div>
      </div>
    </button>
    <div class="accordion-panel" [@accordionSlide]="panelDisplayed ? 'expanded' : 'collapsed'">
      <ng-container *ngTemplateOutlet="panelContent"></ng-container>
    </div>
  }

  <!-- Accordion mode: own expand/collapse header -->

  <!-- Shared panel content template -->
  <ng-template #panelContent>
    <div class="shared-body">
      <issue-body [issue]="activeIssue" [sync-pair]="syncPair" [application]="application"></issue-body>
      <!-- Indicator: which resource's error is currently shown -->
      @if (hasSelectableErrors && activeIssue) {
        <div class="error-source-indicator">
          Showing error for
          <span class="error-source-name">{{ resourceNameString(activeIssue) }}</span>
        </div>
      }
    </div>

    <!-- Single-issue groups don't render the Affected Resources list (resource sits
      in the accordion header instead), so surface the issue's raised time + ID
      here as a permanently-visible compact chip in the bottom-right corner.
      KEEP IN SYNC: mirrors the per-resource right-aligned span (.row-raised-inline)
      used in the multi-resource case above. Same shortTimestamp + shortIssueId
      display and same copyIssueId click handler. -->
    @if (
      subGroup?.issues?.length === 1 && !showResourceList && subGroup.issues[0]?.issueId ? subGroup.issues[0] : null;
      as soleIssue
    ) {
      <div class="panel-meta-row">
        @if (soleIssue.timestamp) {
          <button
            type="button"
            class="row-raised-inline row-raised-inline--static"
            (click)="copyIssueId(soleIssue, $event)"
          >
            {{ shortTimestamp(soleIssue.timestamp) }}
            <span class="row-id-suffix">
              <span class="row-id-sep">·</span>
              <span class="row-id-label">ID:</span>
              <code class="row-id-value">{{ shortIssueId(soleIssue.issueId) }}</code>
            </span>
            @if (copyFeedback.copiedStates['issue-id-' + soleIssue.issueId]) {
              <svg lucideCheck class="row-id-copy-icon row-id-copy-icon--copied"></svg>
            } @else {
              <svg lucideCopy class="row-id-copy-icon"></svg>
            }
          </button>
        }
      </div>
    }

    @if (showResourceList) {
      <div class="affected-resources">
        <div class="resources-toolbar">
          <span class="resources-heading">Affected Resources ({{ subGroup.issues.length }})</span>
        </div>
        @for (group of resourceTypeGroups; track trackByResourceType($index, group)) {
          <div class="resource-type-section">
            <!-- Resource type label removed — redundant since issues are already grouped by type -->
            <div class="resource-entries">
              @for (entry of group.entries; track trackByIssueId(i, entry); let i = $index) {
                <div
                  class="resource-row resource-row--with-details"
                  [class.resource-row--selectable]="hasSelectableErrors"
                  [class.resource-row--active]="isSelectedIssue(entry.issue)"
                  [attr.data-issue-id]="entry.issue.issueId"
                  (click)="hasSelectableErrors ? selectIssue(entry.issue) : null"
                >
                  @if (entry.typeLabel) {
                    <span class="resource-type-tag">{{ entry.typeLabel }}</span>
                  }
                  <span class="inline-copy-block inline-copy-block--wrap">
                    <span class="inline-copy-text">
                      @if (entry.segments.hasDiff) {
                        <span class="seg-common">{{ entry.segments.prefix }}</span
                        ><span class="seg-unique">{{ entry.segments.unique }}</span
                        ><span class="seg-common">{{ entry.segments.suffix }}</span>
                      } @else {
                        {{ entry.segments.full }}
                      }
                    </span>
                  </span>
                  <!-- Locate in resources table -->
                  @if (entry.segments.full) {
                    <button
                      class="locate-btn"
                      (click)="$event.stopPropagation(); resourceClicked.emit(entry.segments.full)"
                      pTooltip="Find in resources table"
                      tooltipPosition="top"
                    >
                      <svg lucideSearch></svg>
                    </button>
                  }
                  @if (entry.consoleUrl) {
                    <a
                      class="console-link-btn"
                      href="{{ entry.consoleUrl }}"
                      target="_blank"
                      rel="noopener"
                      (click)="$event.stopPropagation()"
                      [pTooltip]="'Open in ' + (application?.type === 'azure' ? 'Azure Portal' : 'AWS Console')"
                      tooltipPosition="top"
                    >
                      <svg lucideExternalLink></svg>
                    </a>
                  }
                  <!-- ARN is the only thing that distinguishes two resources sharing a
                       display name, so let the user see (hover) and copy it per row.
                       The issue ID is copyable via the timestamp·ID chip below. -->
                  @if (entry.issue.arn) {
                    <button
                      type="button"
                      class="inline-copy-btn"
                      [copy-clipboard]="entry.issue.arn"
                      (copied)="onResourceArnCopied(i)"
                      (click)="$event.stopPropagation()"
                      [pTooltip]="copyFeedback.copiedStates['arn-' + i] ? 'Copied' : entry.issue.arn"
                      tooltipPosition="top"
                    >
                      @if (copyFeedback.copiedStates['arn-' + i]) {
                        <svg lucideCheck class="copy-check"></svg>
                      } @else {
                        <svg lucideCopy></svg>
                      }
                    </button>
                  }
                  <!-- KEEP IN SYNC: the single-issue panel-footer chip (below, .panel-meta-row)
                      mirrors this same compact "<short timestamp> · ID: <last8>" format
                      and click-to-copy-full-values treatment. Edit both together. -->
                  @if (entry.issue.timestamp) {
                    <button type="button" class="row-raised-inline" (click)="copyIssueId(entry.issue, $event)">
                      {{ shortTimestamp(entry.issue.timestamp) }}
                      <span class="row-id-suffix">
                        <span class="row-id-sep">·</span>
                        <span class="row-id-label">ID:</span>
                        <code class="row-id-value">{{ shortIssueId(entry.issue.issueId) }}</code>
                      </span>
                      @if (copyFeedback.copiedStates['issue-id-' + entry.issue.issueId]) {
                        <svg lucideCheck class="row-id-copy-icon row-id-copy-icon--copied"></svg>
                      } @else {
                        <svg lucideCopy class="row-id-copy-icon"></svg>
                      }
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      @if (flat && !hasAnyResourceName) {
        <span class="no-resource-text">No associated resource</span>
      }
    }
  </ng-template>
</div>
